<template>
    <div
        class="card-shadow rounded-xl border-iron-200 bg-white p-4 ease-out xl:grid xl:grid-cols-[200px_minmax(0,1fr)_auto] xl:items-start xl:gap-6 border transition-all duration-75"
    >
        <div>
            <p class="font-medium text-iron-700">
                {{ formatDay(reservation.start) }}
            </p>
            <p class="text-sm text-iron-500">
                {{ formatFromTo(reservation.start, reservation.end) }}
            </p>
            <p v-if="reservation.createdAt" class="mt-2 text-xs text-iron-400">
                {{
                    $t("bookings.created_at", {
                        date: formatCreatedAt(reservation.createdAt),
                    })
                }}
            </p>
        </div>
        <div class="mt-3 xl:mt-0">
            <p class="font-medium text-iron-700">
                {{ reservation.event_title }}
            </p>
            <p v-if="reservation.firstname" class="text-iron-500">
                {{ reservation.organizer_name }}
                {{ $t("and") }} {{ reservation.firstname }}
                {{ reservation.lastname ? reservation.lastname : "" }}
            </p>
            <p class="text-iron-500">
                {{ reservation.email }}
            </p>

            <div class="mt-3 gap-1.5 flex flex-wrap">
                <div
                    class="rounded-md border-iron-300 px-2 py-1 text-sm font-medium text-iron-600 inline-flex items-center border"
                >
                    <Clock9 :size="18" :stroke-width="1.5" class="mr-1" />
                    {{ calcTimeDifference(reservation.start, reservation.end) }}
                </div>

                <div
                    v-if="reservation.stripeConfig"
                    class="rounded-md border-iron-300 px-2 py-1 text-sm font-medium text-iron-600 inline-flex items-center border"
                >
                    <CircleDollarSign
                        :size="18"
                        :stroke-width="1.5"
                        class="mr-1"
                    />
                    {{
                        `${
                            reservation.stripeConfig.price / 100
                        } ${reservation.stripeConfig.currency}`
                    }}
                </div>

                <a
                    v-if="meetingUrl && reservation.location"
                    :href="meetingUrl"
                    target="_blank"
                    rel="noopener"
                    class="rounded-md border-blue-200 bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100 inline-flex items-center border transition-colors"
                >
                    <div
                        class="mr-2 h-4 w-4"
                        v-html="
                            getLocationForId(reservation.location.type)?.icon
                        "
                    ></div>
                    {{ getLocationForId(reservation.location.type)?.name }}
                    <ExternalLink :size="14" class="ml-1.5" />
                </a>

                <a
                    v-else-if="!reservation.location && meetingUrl"
                    :href="meetingUrl"
                    target="_blank"
                    rel="noopener"
                    class="rounded-md border-blue-200 bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100 inline-flex items-center border transition-colors"
                >
                    <div
                        class="mr-2 h-4 w-4"
                        v-html="getLocationForId('google-meet')?.icon"
                    ></div>
                    Google Meet
                    <ExternalLink :size="14" class="ml-1.5" />
                </a>
            </div>

            <div v-if="isJoinable" class="mt-3">
                <a :href="meetingUrl" target="_blank" rel="noopener">
                    <Button
                        as="span"
                        size="sm"
                        class="gap-2 bg-green-600 text-white hover:bg-green-700"
                    >
                        <span class="h-2 w-2 relative flex">
                            <span
                                class="animate-ping bg-white absolute inline-flex h-full w-full rounded-full opacity-75"
                            ></span>
                            <span
                                class="h-2 w-2 bg-white relative inline-flex rounded-full"
                            ></span>
                        </span>
                        {{ $t("bookings.join_call") }}
                        <span v-if="minutesUntilStart > 0" class="opacity-80">
                            ({{
                                $t("bookings.starts_in_minutes", {
                                    minutes: minutesUntilStart,
                                })
                            }})
                        </span>
                    </Button>
                </a>
            </div>
        </div>
        <div
            class="mt-4 gap-2 border-iron-100 pt-4 sm:justify-end xl:mt-0 xl:border-t-0 xl:pt-0 flex border-t"
        >
            <template v-if="isCancellable">
                <Link
                    v-if="reservation.can_reschedule !== false"
                    :href="getRoute()"
                    class="max-sm:hidden"
                >
                    <Button variant="outline" as="span" size="sm" class="gap-2">
                        <CalendarClock :size="20" :stroke-width="1.75" />
                        {{ $t("bookings.reschedule_link") }}
                    </Button>
                </Link>
                <Button
                    v-else
                    variant="outline"
                    size="sm"
                    disabled
                    class="gap-2 max-sm:hidden"
                    :title="$t('bookings.reschedule_not_allowed')"
                >
                    <CalendarClock :size="20" :stroke-width="1.75" />
                    {{ $t("bookings.reschedule_link") }}
                </Button>
            </template>

            <div class="gap-2 sm:flex-none flex flex-1">
                <Link
                    :href="route('app.reservations.show', reservation.uuid)"
                    class="sm:flex-none flex-1"
                >
                    <Button
                        variant="outline"
                        as="span"
                        size="sm"
                        class="gap-2 w-full"
                    >
                        <ListCollapse :size="20"></ListCollapse>
                        {{ $t("bookings.detail") }}
                    </Button>
                </Link>
                <DropdownMenu v-if="reservation.manageUrl || isCancellable">
                    <DropdownMenuTrigger as-child>
                        <Button
                            variant="outline"
                            size="sm"
                            :aria-label="$t('bookings.more_actions')"
                        >
                            <Ellipsis :size="20" :stroke-width="1.75" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <template v-if="isCancellable">
                            <DropdownMenuItem
                                v-if="
                                    !isWideScreen &&
                                    reservation.can_reschedule !== false
                                "
                                as-child
                            >
                                <Link :href="getRoute()" class="gap-2">
                                    <CalendarClock
                                        :size="16"
                                        :stroke-width="1.75"
                                    />
                                    {{ $t("bookings.reschedule_link") }}
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem @click="cancelEvent">
                                <span
                                    class="gap-2 text-red-600 flex items-center"
                                >
                                    <X :size="16" :stroke-width="1.75" />
                                    {{ $t("bookings.cancel_booking") }}
                                </span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator
                                v-if="reservation.manageUrl"
                            />
                        </template>
                        <template v-if="reservation.manageUrl">
                            <DropdownMenuItem as-child>
                                <a
                                    :href="reservation.manageUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="gap-2"
                                >
                                    <ExternalLink
                                        :size="16"
                                        :stroke-width="1.75"
                                    />
                                    {{ $t("bookings.attendee_page") }}
                                </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem @click="copyManageUrl">
                                <span class="gap-2 flex items-center">
                                    <Copy :size="16" :stroke-width="1.75" />
                                    {{ $t("bookings.copy_attendee_page_link") }}
                                </span>
                            </DropdownMenuItem>
                        </template>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import axios from "axios";
import route from "ziggy-js";
import { isPast, isThisYear } from "date-fns";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";
import { useClipboard, useMediaQuery } from "@vueuse/core";
import {
    CalendarClock,
    X,
    Clock9,
    ListCollapse,
    CircleDollarSign,
    ExternalLink,
    Copy,
    Ellipsis,
} from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/toast/use-toast";
import { useI18n } from "vue-i18n";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import { useLocationOptions } from "../../EventTypes/useLocationOptions";
import { Link } from "@inertiajs/vue3";

const { format } = useLocalisedTzFormat();

const { t, locale } = useI18n();
const { locationOptions, getLocationForId } = useLocationOptions();

// !!Copilot generated - possibly wrong
export type Reservation = {
    id: number;
    uuid: string;
    user_id: number;
    event_id: number;
    eventTypeUuid: string;
    integration_event_id: null;
    type: string;
    email: string;
    phone: null;
    firstname: string;
    lastname: string;
    status: string;
    start: string;
    end: string;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    seats: number;
    data: null;
    googleMeetLink: string;
    zoomLink: string;
    teamsLink: string | null;
    organizer: string;
    organizer_name: string;
    event_title: string;
    customFieldValues: { label: string; value: string }[];
    can_reschedule?: boolean;
    can_cancel?: boolean;
    createdAt?: string | null;
    manageUrl?: string;
    location: {
        type: string;
        value: string;
    };
    stripeConfig?: {
        enabled: boolean;
        integrationId: 160;
        price: 10000;
        currency: string;
    };
    event_type?: {
        id: number;
        uuid: string;
        name: string;
    };
};

const props = defineProps<{
    reservation: Reservation;
}>();

const clipboard2 = useClipboard();
const isWideScreen = useMediaQuery("(min-width: 640px)");
const manageUrlClipboard = useClipboard();
const { toast } = useToast();
const confirm = useConfirm();

const JOIN_WINDOW_MINUTES = 10;

const now = ref(Date.now());
let nowInterval: ReturnType<typeof setInterval>;

onMounted(() => {
    nowInterval = setInterval(() => {
        now.value = Date.now();
    }, 30_000);
});

onUnmounted(() => {
    clearInterval(nowInterval);
});

const meetingUrl = computed(() => {
    if (props.reservation.location?.value) {
        if (
            props.reservation.location.type === "zoom" &&
            props.reservation.zoomLink
        ) {
            return props.reservation.zoomLink;
        }

        if (
            props.reservation.location.type === "microsoft-teams" &&
            props.reservation.teamsLink
        ) {
            return props.reservation.teamsLink;
        }

        if (
            props.reservation.location.type === "google-meet" &&
            props.reservation.googleMeetLink
        ) {
            return props.reservation.googleMeetLink;
        }

        return props.reservation.location.value;
    }

    return props.reservation.googleMeetLink || null;
});

const minutesUntilStart = computed(() => {
    return Math.ceil(
        (new Date(props.reservation.start).getTime() - now.value) / 60_000,
    );
});

const isJoinable = computed(() => {
    if (!meetingUrl.value || props.reservation.status === "canceled") {
        return false;
    }

    const start = new Date(props.reservation.start).getTime();
    const end = new Date(props.reservation.end).getTime();

    return (
        now.value >= start - JOIN_WINDOW_MINUTES * 60_000 && now.value <= end
    );
});

const formatDay = (date: string) => {
    if (locale.value === "cs") {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EEEEEE, dd. MMMM")
            : format(new Date(date), "EEEEEE, dd. MMMM yyyy");
    } else {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EE, d MMM")
            : format(new Date(date), "EE, d MMM yyyy");
    }
};

const formatFromTo = (from: string, to: string) => {
    if (locale.value === "en") {
        return `${format(new Date(from), "h:mm aaa")} - ${format(
            new Date(to),
            "h:mm aaa",
        )}`;
    } else {
        return `${format(new Date(from), "H:mm")} - ${format(
            new Date(to),
            "H:mm",
        )}`;
    }
};

const formatCreatedAt = (date: string) => {
    return locale.value === "en"
        ? format(new Date(date), "MMM d, yyyy, h:mm aaa")
        : format(new Date(date), "d. M. yyyy H:mm");
};

async function copyManageUrl() {
    if (props.reservation.manageUrl) {
        await manageUrlClipboard.copy(props.reservation.manageUrl);
        toast({ title: t("bookings.attendee_page_link_copied") });
    }
}

function getRoute() {
    return (
        route("public.event-types.show", {
            eventTypeUuid: props.reservation.eventTypeUuid,
        }) +
        "?reservationUuid=" +
        props.reservation.uuid
    );
}

const cancelEvent = async () => {
    const succes = await confirm.show({
        type: "danger",

        confirm: t("bookings.cancel_booking"),
        cancel: t("cancel"),

        header: t("bookings.cancel_booking"),
        message:
            t("bookings.are_you_sure") +
            "<br>" +
            t("bookings.cancel_description"),
    });

    if (succes) {
        axios
            .post(route("app.reservations.cancel", props.reservation.id))
            .then(() => {
                window.location.reload();
            });
    }
};

const eventType = computed(() => {
    if (props.reservation.type === "call") {
        return t("bookings.video_call");
    }

    if (props.reservation.type === "personal meeting") {
        return t("bookings.personal_meeting");
    }

    return false;
});

const calcTimeDifference = (from: string, to: string) => {
    const fromTime = new Date(from);
    const toTime = new Date(to);

    const diff = toTime.getTime() - fromTime.getTime();

    const minutes = Math.floor(diff / 1000 / 60);

    return `${minutes} min`;
};

const isCancellable = computed(() => {
    if (props.reservation.status === "canceled") {
        return false;
    }

    if (isPast(new Date(props.reservation.start_date))) {
        return false;
    }

    return true;
});

async function copyRescheduleLink() {
    const response = await axios.post(
        route("app.reservations.signed-urls.store", props.reservation.id),
    );
    await clipboard2.copy(response.data.data);
}
</script>

<style scoped>
.card-shadow:hover {
    box-shadow:
        0px 4px 8px -2px rgba(16, 24, 40, 0.1),
        0px 2px 4px -2px rgba(16, 24, 40, 0.06);
}
</style>
